import axios from "./axiosInstance";

// Base path for organiser API
const BASE_PATH = '/organiser';

/**
 * Handles API errors consistently
 * @param {Error} error - The error object
 * @param {string} context - Context of the error (e.g., 'fetching events')
 * @throws {Error} Enhanced error with user-friendly message
 */
function handleApiError(error, context) {
  console.error(`Error ${context}:`, error);
  
  let message = 'An unexpected error occurred';
  
  if (error.response) {
    // Server responded with a status code outside 2xx
    const { status, data } = error.response;
    message = data?.message || `Server responded with status ${status}`;
    
    if (status === 401) {
      message = 'Session expired. Please log in again.';
      // Optionally handle logout here if needed
    } else if (status === 403) {
      message = 'You do not have permission to perform this action';
    } else if (status === 404) {
      message = 'The requested resource was not found';
    } else if (status >= 500) {
      message = 'Server error. Please try again later.';
    }
  } else if (error.request) {
    // Request was made but no response received
    message = 'No response from server. Please check your connection.';
  }
  
  const errorToThrow = new Error(message);
  errorToThrow.status = error.response?.status;
  errorToThrow.data = error.response?.data;
  throw errorToThrow;
}

export async function fetchOrganiserOverviewMetrics() {
  try {
    const response = await axios.get(`${BASE_PATH}/overview`);
    return {
      totalEvents: Number(response.data?.totalEvents ?? 0),
      completedEvents: Number(response.data?.completedEvents ?? 0),
      activeEvents: Number(response.data?.activeEvents ?? 0),
      averageAttendance: Number(response.data?.averageAttendance ?? 0),
      recentEvents: Array.isArray(response.data?.recentEvents) ? response.data.recentEvents : [],
      eventStatusBreakdown: Array.isArray(response.data?.eventStatusBreakdown) ? response.data.eventStatusBreakdown : [],
      monthlyPerformance: Array.isArray(response.data?.monthlyPerformance) ? response.data.monthlyPerformance : [],
    };
  } catch (error) {
    return handleApiError(error, 'fetching organiser overview metrics');
  }
}

export async function fetchOrganiserRevenueMetrics() {
  try {
    const response = await axios.get(`${BASE_PATH}/revenue`);
    return {
      totalRevenue: Number(response.data?.totalRevenue ?? 0),
      monthlyRevenue: Number(response.data?.monthlyRevenue ?? 0),
      averageRevenuePerEvent: Number(response.data?.averageRevenuePerEvent ?? 0),
      revenueGrowth: Number(response.data?.revenueGrowth ?? 0),
      monthlyTrends: Array.isArray(response.data?.monthlyTrends) ? response.data.monthlyTrends : [],
      revenueByCategory: Array.isArray(response.data?.revenueByCategory) ? response.data.revenueByCategory : [],
      topRevenueEvents: Array.isArray(response.data?.topRevenueEvents) ? response.data.topRevenueEvents : [],
      packagePerformance: Array.isArray(response.data?.packagePerformance) ? response.data.packagePerformance : [],
    };
  } catch (error) {
    return handleApiError(error, 'fetching organiser revenue metrics');
  }
}

/**
 * Validates event data structure
 * @param {Object} event - The event object to validate
 * @returns {boolean} True if valid
 * @throws {Error} If validation fails
 */
function validateEventData(event) {
  if (!event) {
    throw new Error('No event data provided');
  }
  if (!event._id) {
    throw new Error('Event ID is missing');
  }
  return true;
}

/**
 * Fetch all events for the current organizer
 * @returns {Promise<Array>} List of events for the logged-in user
 */
export async function fetchMyEvents() {
  try {
    // Get the logged-in user's ID from local storage
    const userId = localStorage.getItem("userId");
    if (!userId) {
      throw new Error('User not authenticated');
    }

    // Fetch events for the specific user
    const response = await axios.get(`${BASE_PATH}/events`, {
      params: { userId }
    });
    
    if (!Array.isArray(response.data)) {
      throw new Error('Invalid response format: expected an array of events');
    }

    // Ensure each event has a valid _id
    return response.data.map(event => ({
      ...event,
      _id: event._id || event.eventid || event.id || parseInt(event.event_id) || parseInt(event.id)
    }));
  } catch (error) {
    console.error('Error in fetchMyEvents:', error);
    // Return empty array instead of throwing to prevent UI crashes
    return [];
  }
}

/**
 * Get attendance for a specific event
 * @param {string} eventId - The ID of the event
 * @param {boolean} [attendedOnly=true] - Whether to only include attended records
 * @returns {Promise<Array>} List of attendance records
 */
export async function getEventAttendance(eventId, attendedOnly = true) {
  if (!eventId) {
    throw new Error('Event ID is required');
  }
  
  try {
    const response = await axios.get(
      `${BASE_PATH}/events/${encodeURIComponent(eventId)}/attendance`,
      { 
        params: { attended: attendedOnly },
        validateStatus: status => status < 500 // Don't throw for 4xx errors
      }
    );
    
    if (response.status === 404) {
      console.warn(`No attendance data found for event ${eventId}`);
      return [];
    }
    
    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    return handleApiError(error, `fetching attendance for event ${eventId}`);
  }
}

/**
 * Get ticket sales data for a specific event
 * @param {string} eventId - The ID of the event
 * @returns {Promise<Object>} Sales data including over time and distribution
 */
export async function getTicketSales(eventId) {
  if (!eventId) {
    throw new Error('Event ID is required');
  }
  
  const defaultResponse = { salesOverTime: [], ticketDistribution: [] };
  
  try {
    const response = await axios.get(
      `${BASE_PATH}/events/${encodeURIComponent(eventId)}/ticket-sales`,
      { validateStatus: status => status < 500 }
    );
    
    if (response.status === 404) {
      console.warn(`No ticket sales data found for event ${eventId}`);
      return defaultResponse;
    }
    
    return {
      salesOverTime: Array.isArray(response.data?.salesOverTime) 
        ? response.data.salesOverTime 
        : [],
      ticketDistribution: Array.isArray(response.data?.ticketDistribution)
        ? response.data.ticketDistribution
        : []
    };
  } catch (error) {
    console.error(`Error fetching ticket sales for event ${eventId}:`, error);
    return defaultResponse;
  }
}

/**
 * Get ticket sales data over time
 * @param {string} eventId - The ID of the event
 * @returns {Promise<Object>} Ticket sales data over time
 */
export const getTicketSalesOverTime = async (eventId) => {
  try {
    const response = await axios.get(`${BASE_PATH}/events/${eventId}/ticket-sales-over-time`);
    return response.data;
  } catch (error) {
    console.error('Error in getTicketSalesOverTime:', error);
    throw error;
  }
};

/**
 * Create a new event
 * @param {FormData} formData - The event data
 * @returns {Promise<Object>} The created event
 */
export async function createEvent(formData) {
  if (!(formData instanceof FormData)) {
    throw new Error('FormData is required');
  }
  
  try {
    const response = await axios.post(
      `${BASE_PATH}/events`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    
    validateEventData(response.data);
    return response.data;
  } catch (error) {
    return handleApiError(error, 'creating event');
  }
}

/**
 * Update an existing event
 * @param {string} eventId - The ID of the event to update
 * @param {FormData} formData - The updated event data
 * @returns {Promise<Object>} The updated event
 */
export async function updateEvent(eventId, formData) {
  if (!eventId) {
    throw new Error('Event ID is required');
  }
  if (!(formData instanceof FormData)) {
    throw new Error('FormData is required');
  }
  
  try {
    const response = await axios.put(
      `${BASE_PATH}/events/${encodeURIComponent(eventId)}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    
    validateEventData(response.data);
    return response.data;
  } catch (error) {
    return handleApiError(error, `updating event ${eventId}`);
  }
}

/**
 * Rehost an existing event with new dates
 * @param {string} eventId - The ID of the event to rehost
 * @param {Object} rehostData - The rehost data including new dates and packages
 * @returns {Promise<Object>} The rehost response
 */
export async function rehostEvent(eventId, rehostData) {
  if (!eventId) {
    throw new Error('Event ID is required');
  }
  
  if (!rehostData) {
    throw new Error('Rehost data is required');
  }

  try {
    const response = await axios.put(
      `${BASE_PATH}/events/${encodeURIComponent(eventId)}/rehost`,
      rehostData,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
    
    return response.data;
  } catch (error) {
    return handleApiError(error, `rehosting event ${eventId}`);
  }
}

/**
 * Get attendance statistics for an event
 * @param {string} eventId - The ID of the event
 * @returns {Promise<Object>} Object containing totalPurchased and totalAttended counts
 */
export async function getEventAttendanceStats(eventId) {
  try {
    const response = await axios.get(`${BASE_PATH}/events/${eventId}/attendance-stats`);
    return response.data;
  } catch (error) {
    console.error('Error in getEventAttendanceStats:', error);
    throw error;
  }
}

/**
 * Validate ticket and mark attendance
 * @param {string} ticketNumber - The ticket number/ID to validate
 * @param {string} eventId - The event ID to check against
 * @returns {Promise<Object>} Validation result with success status and message
 */
export async function validateTicketAndMarkAttendance(ticketNumber, eventId) {
  if (!ticketNumber || !eventId) {
    throw new Error('Ticket number and event ID are required');
  }

  try {
    const response = await axios.post(`${BASE_PATH}/validate-ticket`, {
      ticketNumber,
      eventId
    });
    
    return response.data;
  } catch (error) {
    return handleApiError(error, `validating ticket ${ticketNumber} for event ${eventId}`);
  }
}

/**
 * Fetch organiser ticket sales analytics data
 * @returns {Promise<Object>} Ticket sales analytics data including total tickets, revenue, trends, etc.
 */
export async function fetchOrganiserTicketSalesAnalytics() {
  try {
    const response = await axios.get(`${BASE_PATH}/ticket-sales`, {
      validateStatus: status => status < 500
    });
    
    return response.data;
  } catch (error) {
    return handleApiError(error, 'fetching organiser ticket sales analytics');
  }
}

/**
 * Fetch organiser time sales analytics data
 * @param {string} eventId - Optional event ID for event-specific analytics
 * @returns {Promise<Object>} Time sales analytics data including time-based sales, hourly trends, etc.
 */
export async function fetchOrganiserTimeSalesAnalytics(eventId = null) {
  try {
    const url = eventId ? `${BASE_PATH}/time-sales?eventId=${encodeURIComponent(eventId)}` : `${BASE_PATH}/time-sales`;
    const response = await axios.get(url, {
      validateStatus: status => status < 500
    });
    
    return response.data;
  } catch (error) {
    return handleApiError(error, 'fetching organiser time sales analytics');
  }
}

/**
 * Fetch organiser event performance analytics data
 * @returns {Promise<Object>} Event performance analytics data including total events, attendance, categories, etc.
 */
export async function fetchOrganiserEventPerformanceAnalytics() {
  try {
    const response = await axios.get(`${BASE_PATH}/event-performance`, {
      validateStatus: status => status < 500
    });
    
    return response.data;
  } catch (error) {
    return handleApiError(error, 'fetching organiser event performance analytics');
  }
}

/**
 * Fetch organiser geographic event distribution data
 * @returns {Promise<Object>} Geographic event distribution data including locations, cities, and regional performance
 */
export async function fetchOrganiserGeographicEventDistribution() {
  try {
    const response = await axios.get(`${BASE_PATH}/geographic-events`, {
      validateStatus: status => status < 500
    });

    return response.data;
  } catch (error) {
    return handleApiError(error, 'fetching organiser geographic event distribution');
  }
}
